/* eslint-disable react-hooks/exhaustive-deps */
import {
  formatPageNumber,
  HBA1C_TABLE,
  LIST_TABLE_CONTENTS_LABEL_DISPLAY,
  SIZE_COVER,
  SCALE,
} from '../../constants';
import LegendsOfChartCom from '../common/LegendsOfChartCom';
import PageFooter from '../common/PageFooter';
import RepeatParticipationBlock from '../common/RepeatParticipationBlock';
import CRPreviewSection from '../common/Section';
import TableCom from '../common/TableCom';
import bgCover from '@/assets/image/pdfexport/bg-cover.png';
import DoughnutChart from '@/new-components/CRReportPDF/components/DoughnutChart';
import ChartDataLabels from '@energiency/chartjs-plugin-piechart-outlabels';
import { Box, Grid, Typography, withStyles } from '@material-ui/core';
import { get, isEmpty, last } from 'lodash';
import React, { useEffect, useMemo } from 'react';

const width = SIZE_COVER.width * SCALE;
const height = SIZE_COVER.height * SCALE;

const CustomContainer = withStyles({
  root: {
    padding: `0 ${80 * SCALE}px`,
    height,
    width,
    margin: '0 auto',
    backgroundImage: `url(${bgCover})`,
    backgroundSize: 'cover',
    backgroundRepeat: 'no-repeat',
    paddingTop: `${64 * SCALE}px`,
  },
})(Box);

const Title = withStyles({
  root: {
    marginTop: 32 * SCALE,
    fontFamily: 'Lato',
    fontWeight: 700,
    fontSize: 16 * SCALE,
    lineHeight: `${24 * SCALE}px`,
  },
})(Typography);

const Item = withStyles({
  root: {
    paddingLeft: `${16 * SCALE}px`,
    borderRight: '1px solid #CCCCCC',
    fontFamily: 'OpenSans',

    '& .title': {
      color: '#666',
      fontWeight: 600,
      fontSize: 10 * SCALE,
    },

    '& .value': {
      fontFamily: 'Lato',
      marginTop: `${4 * SCALE}px`,
      color: '#333',
      fontWeight: 600,
      fontSize: 18 * SCALE,
      lineHeight: `${24 * SCALE}px`,
    },
    '& .subValue': {
      fontFamily: 'OpenSans',
      marginLeft: `${8 * SCALE}px`,
      color: '#333',
      fontWeight: 600,
      fontSize: 12 * SCALE,
      lineHeight: `${18 * SCALE}px`,
    },

    '&:last-child': {
      borderRight: 'none',
    },
  },
})(Grid);

const HSHbA1c = ({ corporateData, addPaging, startPage, totalPages }) => {
  const hba1c = get(corporateData, 'healthScreenings.hbA1c.categories');
  const totalOptOut =
    get(corporateData, 'healthScreenings.hbA1c.totalOptOut') || 0;

  const trendings = corporateData?.healthScreenings?.hbA1c?.trendings;

  const textTrending = () => {
    if (trendings?.length >= 2) {
      let latestYears = trendings?.sort((a, b) => b.year - a.year).slice(0, 2);
      const percentLastYear = latestYears[1].percent;
      const percentThisYear = latestYears[0].percent;
      const lastYear = latestYears[1].year;
      if (percentLastYear === percentThisYear) {
        return 'There are increased cases by 0%.';
      } else if (percentThisYear > percentLastYear) {
        return `There are increased cases by ${Math.abs(
          percentThisYear?.formatPercentValue() -
            percentLastYear?.formatPercentValue()
        )?.formatPercentValue()}%.`;
      } else {
        return `GREAT, we have improved by ${Math.abs(
          percentThisYear?.formatPercentValue() -
            percentLastYear?.formatPercentValue()
        )?.formatPercentValue()}% since ${lastYear}!`;
      }
    } else {
      return 'There are increased cases by 0%.';
    }
  };

  const chartData = useMemo(
    () => hba1c?.map((it) => it.percent.formatPercentValue()),
    [hba1c]
  );

  const backgroundColor = ['#beeec7', '#f9e1a1', '#ffbcb7'];

  const legends = [
    {
      color: '#beeec7',
      title: 'Normal',
    },
    {
      color: '#f9e1a1',
      title: 'Increased Risk',
    },
    {
      color: '#ffbcb7',
      title: 'Diabetes Range',
    },
  ];

  const chartPlugins = [ChartDataLabels];

  const diabetesRangePercent = last(hba1c)?.percent?.formatPercentValue();

  const optOutNote =
    totalOptOut > 0
      ? `*HbA1c result${totalOptOut === 1 ? '' : 's'} for ${totalOptOut} ` +
        `participant${totalOptOut === 1 ? '' : 's'} ` +
        `${totalOptOut === 1 ? 'is' : 'are'} invalid due to opt-out. ` +
        `Hence, the exclusion from reporting.`
      : '';

  const dataTable = useMemo(() => {
    if (isEmpty(hba1c)) return [];
    const table = [...HBA1C_TABLE];
    const dataCutOffs = hba1c?.map((it, inx) => {
      return {
        color: '#F5F5F5',
        value: `${it.cutOffs}`,
        width: table[0][inx + 1]?.width,
        height: 24 * SCALE,
      };
    });

    const dataTotal = hba1c?.map((it, inx) => ({
      color: '#F5F5F5',
      value: `${it.total}`,
      width: table[0][inx + 1]?.width,
      height: 24 * SCALE,
    }));
    const dataPercent = hba1c?.map((it, inx) => ({
      color: '#F5F5F5',
      value: `${it.percent.formatPercentValue()}%`,
      width: table[0][inx + 1]?.width,
      height: 24 * SCALE,
    }));
    let header = [];
    header.push(table[0][0]);
    header = header.concat(
      hba1c?.map((it, inx) => ({
        color: '#E0E0E0',
        value: it.title,
        width: table[0][inx + 1]?.width,
        height: table[0][inx + 1]?.height,
      }))
    );

    table[0] = table[0].map((it, inx) => ({
      ...header[inx],
    }));

    table[1] = table[1].concat(dataCutOffs);
    table[2] = table[2].concat(dataTotal);
    table[3] = table[3].concat(dataPercent);
    return table;
  }, [hba1c]);

  const renderDatas = useMemo(() => {
    let datas = trendings
      ?.sort((a, b) => a.year - b.year)
      .map((it) => ({
        value: `${it.percent?.formatPercentValue()}%`,
        subValue: ` (${it.total} pax)`,
        title: `${it.year}`,
      }));
    return datas;
  }, [trendings]);

  useEffect(() => {
    addPaging(LIST_TABLE_CONTENTS_LABEL_DISPLAY.HealthScreeningFindings);
  }, []);

  return (
    <>
      <CustomContainer>
        <Box display="flex" flexDirection="column" minHeight="100%">
          <Box>
            <CRPreviewSection title="HbA1c" />
          </Box>

          <Box
            fontFamily="OpenSans"
            mt={4 * SCALE}
            fontSize={12 * SCALE}
            lineHeight={`${18 * SCALE}px`}
          >
            <p>
              HbA1c provides an indication of a person’s average blood glucose
              level over the preceding 2–3 months. Unlike a single random blood
              glucose reading, it is less affected by the timing of meals and
              provides a more consistent measure for identifying individuals
              with elevated blood glucose levels.
            </p>
            <p>
              Higher HbA1c levels may indicate an increased risk of diabetes or
              diabetes mellitus. Over time, persistently elevated blood glucose
              can increase the risk of complications including cardiovascular
              disease, kidney disease, visual impairment, nerve damage and foot
              problems.
            </p>
            <p>
              Lifestyle modification, including maintaining a healthy weight,
              regular physical activity and a balanced diet, can benefit both
              individuals with diabetes and those at increased risk of
              developing diabetes.
            </p>
          </Box>

          <Box
            display="flex"
            alignItems="center"
            gridGap={40 * SCALE}
            mt={2 * SCALE}
          >
            <Box width={`${200 * SCALE}px`} height={`${200 * SCALE}px`}>
              <DoughnutChart
                chartData={chartData}
                chartPlugins={chartPlugins}
                backgroundColor={backgroundColor}
                animationDuration={400}
                isLabelOutside
              />
            </Box>
            <Box>
              <LegendsOfChartCom data={legends} />
            </Box>
          </Box>

          <Box
            fontFamily="OpenSans"
            mt={2 * SCALE}
            fontSize={12 * SCALE}
            lineHeight={`${18 * SCALE}px`}
          >
            {diabetesRangePercent}% has HbA1c levels in the Diabetes Range.
          </Box>

          <Box mt={2 * SCALE}>
            <TableCom data={dataTable} />
          </Box>

          {!optOutNote ? null : (
            <Box
              fontFamily="OpenSans"
              mt={2 * SCALE}
              fontSize={12 * SCALE}
              lineHeight={`${18 * SCALE}px`}
            >
              {optOutNote}
            </Box>
          )}

          <PageFooter
            page={formatPageNumber(startPage)}
            totalPages={totalPages}
            title={LIST_TABLE_CONTENTS_LABEL_DISPLAY.HealthScreeningFindings}
          />
        </Box>
      </CustomContainer>

      {/* Trending + Repeat Participation - separate page.
          Not inline like HDL: this page already carries three paragraphs of
          copy plus the opt-out footnote, and the PDF twin has that footnote at
          the exact y the trending block would occupy. */}
      {(trendings ||
        corporateData?.healthScreenings?.hbA1c?.repeatParticipantTrendings !=
          null) && (
        <CustomContainer>
          <Box display="flex" flexDirection="column" minHeight="100%">
            {trendings && (
              <>
                <Title>Trending: High HbA1c Readings</Title>
                <Box
                  fontFamily="OpenSans"
                  mt={2 * SCALE}
                  fontSize={12 * SCALE}
                  lineHeight={`${18 * SCALE}px`}
                >
                  {textTrending()}
                </Box>
                <Box
                  mt={2 * SCALE}
                  display="flex"
                  bgcolor="#E2F2FF"
                  width="100%"
                  height={64 * SCALE}
                  borderRadius={10 * SCALE}
                  py={1.5 * SCALE}
                >
                  <Grid container>
                    {renderDatas?.map((it, inx) => (
                      <Item
                        item
                        xs={renderDatas?.length >= 3 ? 4 : 6}
                        key={`${it.title}-${inx}`}
                      >
                        <div className="title">{it.title}</div>
                        <div className="value">
                          {it.value}

                          <span className="subValue">{it.subValue}</span>
                        </div>
                      </Item>
                    ))}
                  </Grid>
                </Box>
              </>
            )}

            {corporateData?.healthScreenings?.hbA1c
              ?.repeatParticipantTrendings != null && (
              <RepeatParticipationBlock
                data={corporateData?.healthScreenings?.hbA1c}
              />
            )}
            <PageFooter
              page={formatPageNumber(startPage + 1)}
              totalPages={totalPages}
              title={LIST_TABLE_CONTENTS_LABEL_DISPLAY.HealthScreeningFindings}
            />
          </Box>
        </CustomContainer>
      )}
    </>
  );
};

export default HSHbA1c;
